/* eslint-disable default-case */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../elements/Box';
import Button from '../../elements/Button';
import BudgetFilter from './BudgetFilter';
import DateSelectorFilter from './DateSelectorFilter';
import SingleDatePicker from '../DatePickerWidget/SingleDatePicker';
import SingleDateRangePicker from '../DatePickerWidget/SingleDateRangePicker';
import AdvancedFilter from '../AdvancedFilter';
import TravelClassFilter from './TravelClassFilter';
import FlightTypeFilter from './FlightTypeFilter';
import RatingFilter from './RatingFilter';
import TripDurationFilter from './TripDurationFilter';
import { isNotNil, isNilOrEmpty } from '../../../helpers/utils';
import Sorting from './Sorting';
import TravelerInterest from './TravelerInterest';
import AirlinesFilter from './AirlinesFilter';
import OriginDestinationFilter from './OriginDestinationFilter';

import RouteFilter from './RouteFilter';

const SecondaryFilters = (
  {
    filter,
    containerSettings,
    containerFiltersSetting,
    budgetSettings,
    budgetColumnSettings,
    availableFilters,
    dateRange,
    budget,
    travelClass,
    onClearFiltersSecondary,
    clearAllSettings,
    clearAllColumnSettings,
    dateRangeSettings,
    dateRangeColumnSettings,
    travelClassColumnSettings,
    travelClassSettings,
    flightType,
    advanced,
    advancedFiltersSettings,
    advancedColumnSettings,
    locations,
    originDestinationSettings,
    OriginDestinationColumnSettings,
    flightTypeSettings,
    flightTypeColumnSettings,
    routeColumnSettings,
    sortingDisplay,
    sorting,
    sortingSettings,
    sortingColumnSettings,
    sortingBoxSettings,
    showClearButton = true,
    rating,
    ratingSettings,
    ratingColumnSettings,
    tripDuration,
    tripDurationSettings,
    tripDurationColumnSettings,
    travelerInterest,
    travelerInterestSettings,
    travelerInterestColumnSettings,
    routeSettings,
    airlines,
    airlinesColumnSettings,
    airlinesSettings,
    loading
  },
  ref
) => {
  const locationComponents = {
    ...originDestinationSettings.components
  };
  const { translate } = React.useContext(Context);
  const elementsWrappers = {
    OriginDestination: (e) => e,

    Route: (e) => (
      <Box key="Route" marginRight="8px" marginBottom="12px" floatLeft {...routeColumnSettings}>
        {e}
      </Box>
    ),
    Budget: (e) => (
      <Box key="Budget" marginRight="8px" marginBottom="12px" floatLeft {...budgetColumnSettings}>
        {e}
      </Box>
    ),
    FlightType: (e) => (
      <Box
        key="FlightType"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...flightTypeColumnSettings}
      >
        {e}
      </Box>
    ),
    TravelClass: (e) => (
      <Box
        key="TravelClass"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...travelClassColumnSettings}
      >
        {e}
      </Box>
    ),
    DatePicker: (e) => (
      <Box
        key="DatePicker"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...dateRangeColumnSettings}
      >
        {e}
      </Box>
    ),
    DateRangeSelector: (e) => (
      <Box
        key="DatePicker"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...dateRangeColumnSettings}
      >
        {e}
      </Box>
    ),
    AdvancedFilter: (e) => (
      <Box
        key="AdvancedFilter"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...advancedColumnSettings}
      >
        {e}
      </Box>
    ),
    Rating: (e) => (
      <Box
        key="RatingFilter"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...ratingColumnSettings}
      >
        {e}
      </Box>
    ),
    TripDuration: (e) => (
      <Box
        key="TripDurationFilter"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...tripDurationColumnSettings}
      >
        {e}
      </Box>
    ),
    Sorting: (e) => (
      <Box key="Sorting" {...sortingColumnSettings}>
        {e}
      </Box>
    ),
    TravelerInterest: (e) => (
      <Box key="TravelerInterest" {...travelerInterestColumnSettings}>
        {e}
      </Box>
    ),
    Airlines: (e) => (
      <Box
        key="Airlines"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...airlinesColumnSettings}
      >
        {e}
      </Box>
    )
  };

  const elements = {
    Budget: <BudgetFilter {...budgetSettings} {...budget} />,
    FlightType: <FlightTypeFilter {...flightTypeSettings} {...flightType} />,
    TravelClass: <TravelClassFilter {...travelClassSettings} {...travelClass} />,
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestinationFilter"
        {...originDestinationSettings}
        components={locationComponents}
        {...locations}
        // isDirty={wasClear}
        // setWasClear={setWasClear}
      />
    ),

    Route: (
      <RouteFilter
        filter={filter}
        originDestinationSettings={originDestinationSettings}
        locations={locations}
        {...routeSettings}
      />
    ),
    DatePicker: (
      <DateSelectorFilter
        ItemComponentInstance={SingleDatePicker}
        dateRangeSettings={dateRangeSettings}
        dateRange={dateRange}
      />
    ),
    DateRangeSelector: (
      <DateSelectorFilter
        ItemComponentInstance={SingleDateRangePicker}
        dateRangeSettings={dateRangeSettings}
        dateRange={dateRange}
      />
    ),
    AdvancedFilter: <AdvancedFilter {...advanced} {...advancedFiltersSettings} />,
    Rating: <RatingFilter {...rating} {...ratingSettings} />,
    TripDuration: <TripDurationFilter {...tripDuration} {...tripDurationSettings} />,
    Sorting: <Sorting {...sortingSettings} {...sorting} loading={loading} />,
    TravelerInterest: <TravelerInterest {...travelerInterest} />,
    Airlines: (
      <AirlinesFilter
        {...airlines}
        {...airlinesSettings}
        origin={locations.origin}
        destination={locations.destination}
      />
    )
  };

  /**
   * It is necessary to create the payload of only the active filters,
   * because clearAll only affects the secondary filters, never the primary ones.
   *
   * IMPORTANT: As new filters are added, the way in which said
   * filters are cleaned must also be added.
   */
  const handleClear = () => {
    let newPayload = {};
    availableFilters.forEach((filter, index) => {
      let element = filter;
      if (typeof element === 'object' && filter.type) {
        element = filter.type;
      }
      // eslint-disable-next-line default-case
      switch (element) {
        case 'Budget': {
          newPayload.budget = null;
          break;
        }
        case 'DatePicker':
        case 'DateRangeSelector': {
          newPayload.dateRange = {};
          break;
        }
        case 'Route': {
          newPayload.origin = {};
          newPayload.destination = {};
          break;
        }
        case 'OriginDestination': {
          newPayload.origin = {};
          newPayload.destination = {};
          break;
        }
        case 'AdvancedFilter': {
          newPayload.tripDuration = null;
          const minimum = advancedFiltersSettings?.ratingSettings?.minValue;
          const maximum = advancedFiltersSettings?.ratingSettings?.starCount;
          minimum && minimum > 0 && maximum
            ? (newPayload.rating = { minimum: minimum, maximum: maximum })
            : (newPayload.rating = null);

          break;
        }
        case 'Rating': {
          const minimum = ratingSettings?.minValue;
          const maximum = ratingSettings?.starCount;
          minimum && minimum > 0 && maximum
            ? (newPayload.rating = { minimum: minimum, maximum: maximum })
            : (newPayload.rating = null);
          break;
        }
        case 'TripDuration': {
          newPayload.tripDuration = null;
          break;
        }
        case 'TravelerInterest': {
          newPayload.travelerInterest = {};
          if (index === 0) {
            firstElementRef.current.clearInput();
          } else {
            travelerInterestRef.current.clearInput();
          }
          break;
        }
        case 'Airlines': {
          newPayload.airlines = null;
          break;
        }
      }
    });

    setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    onClearFiltersSecondary(newPayload);
  };

  const filtersChanged = React.useMemo(() => {
    let filtersChange = !!(isNotNil(budget.value) || travelClass.isChanged || flightType.isChanged);

    availableFilters.forEach((i) => {
      let item = i;
      if (typeof item === 'object') {
        item = i.type;
      }
      switch (item) {
        case 'Route': {
          filtersChange = !!(
            locations.origin.value ||
            locations.destination.value ||
            filtersChange
          );
          break;
        }
        case 'OriginDestination': {
          filtersChange = !!(
            locations.origin.value ||
            locations.destination.value ||
            filtersChange
          );
          break;
        }
        case 'DatePicker':
        case 'DateRangeSelector': {
          filtersChange = !!(dateRange.value.start || dateRange.value.end || filtersChange);
          break;
        }
        case 'AdvancedFilter': {
          filtersChange = !!(advanced.tripDuration.value || advanced.rating.value || filtersChange);
          break;
        }
        case 'Rating': {
          filtersChange = !!(rating.value || filtersChange);
          break;
        }
        case 'TripDuration': {
          filtersChange = !!(tripDuration.value || filtersChange);
          break;
        }
        case 'Sorting': {
          filtersChange = !!(sorting.isChanged || filtersChange);
          break;
        }
        case 'TravelerInterest': {
          //Validar cuando vengan multiples travelerInterest
          filtersChange = !!(!isNilOrEmpty(travelerInterest?.selectedItems) || filtersChange);
          break;
        }
      }
    });

    return filtersChange;
  }, [
    budget.value,
    travelClass.isChanged,
    flightType.isChanged,
    availableFilters,
    locations.origin.value,
    locations.destination.value,
    dateRange.value.start,
    dateRange.value.end,
    advanced?.tripDuration?.value,
    advanced?.rating?.value,
    rating?.value,
    tripDuration?.value,
    sorting.isChanged,
    travelerInterest?.selectedItems
  ]);

  const ClearButton = () => (
    <Box floatLeft {...clearAllColumnSettings}>
      <Button
        onClick={handleClear}
        disabled={!filtersChanged}
        cursor={!filtersChanged ? 'default' : 'pointer'}
        dataTestId="clearAllBtn"
        ariaLabel={translate('cmp-secondary-filter-clear-button-accessibility-message', 'Clear')}
        {...clearAllSettings}
      >
        {translate('cmp-secondary-filter-clear', 'Clear')}
      </Button>
    </Box>
  );

  const filtersToDisplay = availableFilters.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator] && (sortingDisplay === 'inline' || iterator !== 'Sorting');
  });

  const firstElementRef = useRef(null);
  const travelerInterestRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    focusFirstElement: () => {
      setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    }
  }));

  return (
    <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
      <Box display="inline-block" {...containerFiltersSetting}>
        {filtersToDisplay.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);

          if (index === 0) {
            if (e === 'TravelerInterest') {
              const temporalElement = React.cloneElement(elements[e], {
                ref: firstElementRef,
                ...travelerInterestSettings[ele.id]
              });
              return wrapper(temporalElement);
            }
            const temporalElement = React.cloneElement(elements[e], { ref: firstElementRef });
            return wrapper(temporalElement);
          } else {
            if (e === 'TravelerInterest') {
              const temporalElement = React.cloneElement(elements[e], {
                ...travelerInterestSettings[ele.id],
                ref: travelerInterestRef
              });
              return wrapper(temporalElement);
            }
            return element;
          }
        })}
        {showClearButton && <ClearButton />}
      </Box>
      {sortingDisplay === 'below-filters' && (
        <Box {...sortingBoxSettings}>{elements['Sorting']}</Box>
      )}
    </Box>
  );
};

SecondaryFilters.propTypes = {
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(
    PropTypes.oneOf([
      'DateRangeSelector',
      'DatePicker',
      'Passengers/TravelClass',
      'TravelClass',
      'Budget',
      'FlightType',
      'Route',
      'Sorting',
      'AdvancedFilter',
      'Rating',
      'TripDuration',
      'Airlines',
      PropTypes.objectOf(PropTypes.any)
    ])
  ),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  travelClassSettings: PropTypes.objectOf(PropTypes.any),
  travelClassColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeColumnSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeColumnSettings: PropTypes.objectOf(PropTypes.any),
  budgetSettings: PropTypes.objectOf(PropTypes.any),
  budgetColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingSettings: PropTypes.objectOf(PropTypes.any),
  sortingColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingRowSettings: PropTypes.objectOf(PropTypes.any),
  airlinesSettings: PropTypes.objectOf(PropTypes.any),
  airlinesColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingDisplay: PropTypes.oneOf(['inline', 'below-filters']),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  dateRange: PropTypes.shape({
    value: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    defaultValue: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    onChange: PropTypes.func,
    onClear: PropTypes.func
  }),
  budget: PropTypes.shape({
    value: PropTypes.number,
    defaultValue: PropTypes.number,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    currencyCode: PropTypes.string
  }),
  travelClass: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  flightType: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  sorting: PropTypes.shape({
    selectedOption: PropTypes.string,
    onChange: PropTypes.func
  }),
  onClear: PropTypes.func,
  loading: PropTypes.bool
};

SecondaryFilters.defaultProps = {
  loading: false,
  sortingDisplay: 'inline',
  availableFilters: ['Budget', 'DatePicker'],
  showLabels: false,
  containerSettings: {},
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true
  },
  clearAllColumnSettings: {},
  originDestinationSettings: {},
  travelClassSettings: {},
  travelClassColumnSettings: {},
  flightTypeSettings: {},
  flightTypeColumnSettings: {},
  dateRangeSettings: {},
  dateRangeColumnSettings: {},
  budgetSettings: {},
  budgetColumnSettings: {},
  sortingSettings: {},
  sortingColumnSettings: {},
  sortingRowSettings: {},
  locations: { origin: {}, destination: {} },
  travelClass: {},
  flightType: {},
  dateRange: {},
  budget: {},
  sorting: {},
  onClear: null
};

export default React.forwardRef(SecondaryFilters);
